import FluxUtils from 'flux/utils';
import Immutable from 'immutable';
import React from 'react';

import TablePreferencesActions from 'sharedreact/actions/TablePreferencesActions';
import Table from 'sharedreact/components/Table';
import SortOrder from 'sharedreact/data/SortOrder';
import TableColumn from 'sharedreact/data/TableColumn';
import type TableSortPreferences from 'sharedreact/data/TableSortPreferences';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import SettingsStore from 'sharedreact/stores/SettingsStore';
import TablePreferencesStore from 'sharedreact/stores/TablePreferencesStore';
import asFunctional from 'sharedreact/components/asFunctional';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import { RowType } from 'sharedreact/components/TableRow';
import { assertNonNullish } from 'sharedreact/utils/TypeSupport';

function handleToggleColumnSelection<T> (
  tableName: string,
  columnName: string,
  tableColumns: Immutable.List<TableColumn<T>>,
  sortOrder?: SortOrder,
  onSortOrderChange?: (sortOrder: SortOrder) => void
): void {
  let selectedColumns: Immutable.Set<string> = Immutable.Set(tableColumns.filter(col => col.selectable).map(col => col.name ?? ''));

  if (selectedColumns.includes(columnName)) {
    // Column should be removed
    selectedColumns = selectedColumns.delete(columnName);

    const columnToDelete: TableColumn<T> | undefined = tableColumns.find(col => col.name === columnName);
    if (
      columnToDelete != null && sortOrder != null &&
      columnToDelete.sortOrderKeyword != null &&
      columnToDelete.sortOrderKeyword.length > 0 &&
      columnToDelete.sortOrderKeyword === sortOrder.keyword) {
      const newSortColumn = tableColumns.find(col =>
        col.sortOrderKeyword != null &&
        col.sortOrderKeyword.length > 0 &&
        col.sortOrderKeyword !== sortOrder.keyword);

      if (newSortColumn != null && newSortColumn.sortOrderKeyword != null) {
        const sortPreferences = TablePreferencesStore.getSortPreferences(tableName);
        const ascending = sortPreferences != null ? sortPreferences.getAscending(newSortColumn.sortOrderKeyword) : true;

        const newSortOrder: SortOrder = new SortOrder({keyword: newSortColumn.sortOrderKeyword, ascending: ascending});

        TablePreferencesActions.setTableSortOrder(tableName, newSortOrder);

        if (onSortOrderChange != null) {
          onSortOrderChange(newSortOrder);
        }
      }
    }
  } else {
    // Column should be added
    selectedColumns = selectedColumns.add(columnName);
  }

  TablePreferencesActions.setSelectedColumns(tableName, selectedColumns);
}

function handleColumnWidthsChange<T> (tableName: string, widths: Immutable.List<string>, tableColumns: Immutable.List<TableColumn<T>>): void {
  if (widths.size !== tableColumns.size) {
    return;
  }

  const columnWidths:{[name: string]: string} = {};
  for (let i = 0; i < tableColumns.size; i++) {
    const tableColumn = tableColumns.get(i)!;
    if (tableColumn.name != null) {
      const columnName = tableColumn.name;
      columnWidths[columnName] = widths.get(i) ?? '0';
    }
  }

  TablePreferencesActions.setColumnWidths(tableName, Immutable.Map(columnWidths));
}

function handleMoveColumn<T> (tableName:string, columnToMove: number, moveTo: number, tableColumns: Immutable.List<TableColumn<T>>, columnOrder: Immutable.List<string>): void {
  if (tableColumns.has(columnToMove) && tableColumns.get(moveTo)) {
    const columnName1: string = tableColumns.get(columnToMove)!.name ?? '';
    const columnName2: string  = tableColumns.get(moveTo)!.name ?? '';

    const index1: number = columnOrder.indexOf(columnName1);
    const index2: number = columnOrder.indexOf(columnName2);

    TablePreferencesActions.setColumnOrder(tableName, columnOrder.set(index1, columnName2).set(index2, columnName1));
  }
}

function handleSortOrderChange (tableName: string, sortOrder: SortOrder, onSortOrderChange?: (sortOrder: SortOrder) => void): void {
  TablePreferencesActions.setTableSortOrder(tableName, sortOrder);

  if (onSortOrderChange != null) {
    onSortOrderChange(sortOrder);
  }
}

function syncSortOrderWithPreferences (sortPreferences: TableSortPreferences, onSortOrderChange: (sortOrder: SortOrder) => void): void {
  const newSortOrder: SortOrder = new SortOrder({
    keyword: sortPreferences.activeKeyword,
    ascending: sortPreferences.getAscending(sortPreferences.activeKeyword)
  });
  if (AppDispatcher.isDispatching()) {
    setTimeout(() => onSortOrderChange(newSortOrder));
  } else {
    onSortOrderChange(newSortOrder);
  }
}

type Props<T extends RowType> = {
  id: string,
  className?: string,
  tableName?: string,
  tableColumns: Immutable.List<TableColumn<T>>,
  allTableColumns?: Immutable.List<TableColumn<T>>,
  sortOrder?: SortOrder,
  rowHeight?: number,
  rowData: Immutable.List<T | null>,
  realTotalCount?: number,
  isInitialized?: boolean,
  isFetching?: boolean,
  selection?: Immutable.Set<string>,
  selectionIndexes?: Immutable.Map<string, number>,
  disabledForSelection?: Immutable.List<string>,
  highlightId?: string,
  highlightIndex?: number,
  showSelection?: boolean,
  ensureSelectionIsVisible?: boolean,
  emptyTableText?: string,
  noTableHeader?: boolean,

  setVisibleRange?: (range: RangeType) => void,
  onItemSelect?: (id: string) => void,
  onHover?: (sourceItem: any, targetItem: T, position: string) => void,
  onDrop?: (sourceItem: any, targetItem: T, position: string) => void,
  onSortOrderChange?: (sortOrder: SortOrder) => void,
  onEnsuredSelectionIsVisible?: () => void,
  onToggleColumnSelection?: (columnName: string) => void,
  onColumnWidthsChange?: (widths: Immutable.List<string>) => void,
  onMoveColumn?: (columnToMove: number, moveTo: number) => void,
  getClassNameOnDragHover?: (sourceItem: any, targetItem: T, position: string) => string,
  clearHighlightId?: (id: string) => void,
  itemCanDragDrop?: (item: T) => boolean,
  itemGetDragDropItemType?: (item: T) => string,
  itemAcceptsDropItemTypes?: (item: T) => string[],
  isSelected?: (item: T, selection: Immutable.Set<string>) => boolean
};

type State<T extends RowType> = ConstructorParameters<typeof Table<T>>[0];

function getStores () {
  return [
    TablePreferencesStore,
    SettingsStore
  ];
}

function calculateState<T extends RowType> (prevState?: State<T>, props?: Props<T>): State<T> {
  assertNonNullish(props);

  let selectedColumns: Immutable.Set<string> | undefined = undefined;
  let columnWidths: Immutable.Map<string, string> | undefined = undefined;
  let columnOrder: Immutable.List<string> | undefined = undefined;
  let sortPreferences: TableSortPreferences | undefined = undefined;
  if (props.tableName != null) {
    selectedColumns = TablePreferencesStore.getSelectedColumns(props.tableName);
    columnWidths = TablePreferencesStore.getColumnWidths(props.tableName);
    columnOrder = TablePreferencesStore.getColumnOrder(props.tableName);
    sortPreferences = TablePreferencesStore.getSortPreferences(props.tableName);
  }

  if (
    props.onSortOrderChange &&
    prevState != null &&
    prevState.sortPreferences == null &&
    sortPreferences != null &&
    sortPreferences.activeKeyword != null &&
    sortPreferences.activeKeyword.length > 0
  ) {
    if (
      props.sortOrder == null ||
      props.sortOrder.keyword !== sortPreferences.activeKeyword ||
      props.sortOrder.ascending !== sortPreferences.getAscending(sortPreferences.activeKeyword)
    ) {
      syncSortOrderWithPreferences(sortPreferences, props.onSortOrderChange);
    }
  }

  let orderedTableColumns: Immutable.List<TableColumn<T>>;
  if (columnOrder == null) {
    orderedTableColumns = props.tableColumns;
  } else {
    let remainingTableColumns = props.tableColumns;
    orderedTableColumns = Immutable.List();
    for (const columnName of columnOrder) {
      let index = -1;
      if (columnName === '') {
        index = remainingTableColumns.findIndex(tableColumn => tableColumn.name == null);
      } else {
        index = remainingTableColumns.findIndex(tableColumn => tableColumn.name === columnName);
      }
      if (index >= 0 && remainingTableColumns.get(index)) {
        orderedTableColumns = orderedTableColumns.push(remainingTableColumns.get(index)!);
        remainingTableColumns = remainingTableColumns.delete(index);
      }
    }
    if (remainingTableColumns.size > 0) {
      orderedTableColumns = orderedTableColumns.concat(remainingTableColumns);
    }
  }
  let actualColumnOrder = orderedTableColumns.map(tableColumn => tableColumn.name == null ? '' : tableColumn.name);

  let tableColumns: Immutable.List<TableColumn<T>> = orderedTableColumns.filter((col) => {
    if (selectedColumns != null && col.name != null && col.selectable) {
      return selectedColumns.includes(col.name);
    } else {
      return col.default == null || col.default;
    }
  });
  tableColumns = tableColumns.map((col) => {
    if (columnWidths != null && col.name != null) {
      const width = columnWidths.get(col.name);
      if (width != null) {
        col.width = width;
      }
    }

    return col;
  });

  let rowHeight: number = 53;
  if (props.rowHeight != null) {
    rowHeight = props.rowHeight;
  }

  const uiSize: string = SettingsStore.getState().settings.uiSize;
  if (uiSize === 'small') {
    rowHeight = 36;
  } else if (uiSize === 'extrasmall') {
    rowHeight = 32;
  }

  return {
    id: props.id,
    language: SettingsStore.getLanguage(),
    className: props.className,
    tableColumns: tableColumns,
    rowData: props.rowData,
    isInitialized: props.isInitialized,
    isFetching: props.isFetching,
    selection: props.selection,
    disabledIds: props.disabledForSelection,
    highlightId: props.highlightId,
    highlightIndex: props.highlightIndex,
    showSelection: props.showSelection,
    sortOrder: props.sortOrder,
    ensureSelectionIsVisible: props.ensureSelectionIsVisible,
    selectionIndexes: props.selectionIndexes,
    emptyTableText: props.emptyTableText,
    rowHeight: rowHeight,
    allTableColumns: props.tableColumns,
    sortPreferences: sortPreferences,
    noTableHeader: props.noTableHeader,
    setVisibleRange: props.setVisibleRange,
    onItemSelect: props.onItemSelect,
    onHover: props.onHover,
    onDrop: props.onDrop,
    onSortOrderChange: props.tableName != null
      ? (sortOrder) => handleSortOrderChange(props.tableName!, sortOrder, props.onSortOrderChange)
      : props.onSortOrderChange,
    onEnsuredSelectionIsVisible: props.onEnsuredSelectionIsVisible,
    onToggleColumnSelection: props.tableName != null
      ? (columnName) => handleToggleColumnSelection(props.tableName!, columnName, tableColumns, props.sortOrder, props.onSortOrderChange)
      : undefined,
    onColumnWidthsChange: props.tableName != null
      ? (widths) => handleColumnWidthsChange(props.tableName!, widths, tableColumns)
      : undefined,
    onMoveColumn: props.tableName != null
      ? (columnToMove, moveTo) => handleMoveColumn(props.tableName!, columnToMove, moveTo, tableColumns, actualColumnOrder)
      : undefined,
    clearHighlightId: props.clearHighlightId,
    getClassNameOnDragHover: props.getClassNameOnDragHover,
    itemCanDragDrop: props.itemCanDragDrop,
    itemGetDragDropItemType: props.itemGetDragDropItemType,
    itemAcceptsDropItemTypes: props.itemAcceptsDropItemTypes,
    realTotalCount: props.realTotalCount,
    isSelected: props.isSelected
  };
}

class TableContainer<T extends RowType> extends React.PureComponent<Props<T>> {
  private _wrappedContainer: FluxUtils.Container.Component<Props<T>, State<T>, any>;

  constructor (props: Props<T>) {
    super(props);

    this._wrappedContainer = FluxUtils.Container.createFunctional<Props<T>, State<T>>(
      asFunctional(Table<T>),
      getStores,
      calculateState,
      { withProps: true }
    );
  }
  
  override render (): React.ReactNode {
    const WrappedContainer = this._wrappedContainer;

    return (
      <WrappedContainer {...this.props} />
    );
  }
}

export default TableContainer;
